import update from 'immutability-helper';
import { useEffect, useState } from 'react';
import { useNavMenuSidebar } from '@anytag/shared-atoms';
import { useQueryHelper } from '@anytag/shared-hooks';
import { ChatTalker, useGeminiChatHistoryLazyQuery } from '@anytag/graphql';
import { ErrorsProps, MessageType } from '../chat-room';
import { useManageChatThread } from './use-manage-chat-thread';

const DEFAULT_CHAT_HISTORY_LIMIT = 20;

export const useAIAssistantStates = () => {
  const navMenuSidebar = useNavMenuSidebar();
  const { t, enqueueSnackbar } = useQueryHelper();
  const [defaultTemplate, setDefaultTemplate] = useState('');
  // TODO: openAI is deactivated on the BE side, we don't need to use it unless we will fully implement it
  // const { getAiChatModelQuery } = useAiChatModelLazyQuery();
  const [messages, setMessages] = useState<MessageType[]>([]);
  const [error, setError] = useState<(ErrorsProps & { runId?: string }) | null>(null);
  const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);

  const isOpenAiModel = false; // TODO: openAI is deactivated on the BE side, we don't need to use it unless we will fully implement it

  const {
    threadId,
    isChatHistoryOpen,
    isCreatingChatAi,
    isAiSidebarOpen,
    createChatAiThread,
    closeChatAiThread,
    setAiAssistantState
  } = useManageChatThread({
    isOpenAiModel,
    onCreateError: (message) => {
      setError({ message });
    }
  });
  // If we receive a shared url has threadId available, we need to load chat history instead of displaying the templates
  const [chatHistoryThreadId, setChatHistoryThreadId] = useState<number | null>(threadId ?? null);
  const { getGeminiChatHistory } = useGeminiChatHistoryLazyQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const historyMessages = data?.geminiChatHistory?.messages;

      if (historyMessages?.length) {
        updateMessages(
          historyMessages.reduceRight<MessageType[]>((acc, curr) => {
            const isModel = curr.who === ChatTalker.MODEL;

            return [
              ...acc,
              {
                copyable: isModel,
                text: curr.message,
                regenerative: isModel,
                position: isModel ? 'left' : 'right'
              } satisfies MessageType
            ];
          }, []),
          true // Push history messages to the top of the list
        );

        if (historyMessages.length < DEFAULT_CHAT_HISTORY_LIMIT) {
          setChatHistoryThreadId(null);
        }
      } else {
        setChatHistoryThreadId(null);
      }
      setIsLoadingChatHistory(false);
    },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      setChatHistoryThreadId(null);
      setIsLoadingChatHistory(false);
    }
  });

  useEffect(() => {
    if (chatHistoryThreadId) {
      // ThreadId can be null or valid
      // if null: user open chat but not send anything and pick a history chat
      // if valid: user created a thread then change to another chat history
      if (threadId !== chatHistoryThreadId) {
        refreshChat();
        setAiAssistantState({ threadId: chatHistoryThreadId, isChatHistoryOpen: false, isAiSidebarOpen: true });
      } else if (!messages.length) {
        setIsLoadingChatHistory(true);
        // wait for typing message display a bit before loading chat history
        setTimeout(() => {
          getChatHistory();
        }, 2000);
      }
    }
  }, [threadId, messages.length, chatHistoryThreadId]);

  const removeLastTypingMessage = () => {
    setMessages((prevMessages) => {
      const lastMessage = prevMessages.at(-1);

      return lastMessage?.typing ? update(prevMessages, { $splice: [[-1, 1]] }) : prevMessages;
    });
  };

  const clearError = () => {
    setError(null);
    removeLastTypingMessage();
  };

  const updateMessages = (newMessages: MessageType[], unshift = false) => {
    setMessages((prevMessages) => {
      const prevMessage = prevMessages.at(-1);

      return prevMessages.length > 0 && prevMessage?.typing
        ? update(prevMessages, {
            $splice: [[-1, 1]], // Remove typing message
            ...(unshift ? { $unshift: newMessages } : { $push: newMessages })
          })
        : update(prevMessages, {
            ...(unshift ? { $unshift: newMessages } : { $push: newMessages })
          });
    });
  };

  const clearMessages = (fromIndex: number) => {
    if (fromIndex <= 0) {
      setMessages([]);
    } else {
      setMessages((prevMessages) => update(prevMessages, { $splice: [[fromIndex]] }));
    }
  };

  const closeChat = () => {
    setAiAssistantState({ isAiSidebarOpen: false });
    navMenuSidebar.setSidebarState({ isOpen: true });
    setMessages([]);
    setError(null);
    closeChatAiThread(); // without await - Smoothly close the chat thread
    setAiAssistantState({ threadId: null, isChatHistoryOpen: false, isAiSidebarOpen: false });
  };

  const refreshChat = () => {
    setMessages([]);
    setError(null);
    setDefaultTemplate('');
    closeChatAiThread(); // without await - Smoothly close the chat thread
    setAiAssistantState({ threadId: null, isChatHistoryOpen: false, isAiSidebarOpen: true });
  };

  const getChatHistory = async () => {
    if (!threadId || !chatHistoryThreadId || isLoadingChatHistory) {
      return;
    }

    try {
      if (isOpenAiModel) {
        enqueueSnackbar('Not support get chat history for OpenAI model yet', { variant: 'error' });
        return;
      } else {
        await getGeminiChatHistory({
          variables: {
            id: threadId,
            limit: DEFAULT_CHAT_HISTORY_LIMIT,
            offset: messages.at(-1)?.typing ? messages.length - 1 : messages.length
          }
        });
      }
    } catch (error) {
      enqueueSnackbar('Cannot get chat history', { variant: 'error' });
    } finally {
      removeLastTypingMessage();
    }
  };

  return {
    clearError,
    clearMessages,
    closeChat,
    error,
    isAiSidebarOpen,
    isChatHistoryOpen,
    isCreatingChatAi,
    isLoadingChatHistory,
    isOpenAiModel,
    getChatHistory,
    defaultTemplate,
    messages,
    chatHistoryThreadId,
    refreshChat,
    setChatHistoryThreadId,
    createChatAiThread,
    removeLastTypingMessage,
    setAiAssistantState,
    setError,
    threadId,
    updateMessages,
    setDefaultTemplate
  };
};
